/**
 * components/ChartDialog.js
 *
 * Full-size chart dialog opened when the user clicks "View Chart" in a
 * chatbot assistant message. Provides:
 *   • Large chart view (maxWidth="lg", ~1200px)
 *   • Fullscreen toggle (normal ↔ fullscreen)
 *   • SVG download button
 *   • Close button
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle,
  Divider, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  BarChartOutlined,
  Close,
  FileDownloadOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from '@mui/icons-material';
import ChatChartPanel from './ChatChartPanel';

export default function ChartDialog({ open, onClose, chartType, chartData, chartTitle, yAxisLabel }) {
  const [fullscreen, setFullscreen] = useState(false);
  const chartRef = useRef(null);

  const handleDownload = useCallback(() => {
    const svg = chartRef.current?.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `hrms-chart-${chartType || 'export'}-${Date.now()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [chartType]);

  const handleClose = () => {
    setFullscreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullscreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : 3,
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
          px: 2,
          minHeight: 56,
        }}
      >
        <BarChartOutlined sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {chartTitle || 'Analytics Chart'}
        </Typography>

        <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <IconButton
            size="small"
            onClick={() => setFullscreen((f) => !f)}
            sx={{ color: 'text.secondary' }}
          >
            {fullscreen
              ? <FullscreenExitOutlined fontSize="small" />
              : <FullscreenOutlined    fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Download chart">
          <IconButton
            size="small"
            onClick={handleDownload}
            sx={{ color: 'text.secondary' }}
          >
            <FileDownloadOutlined fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Close">
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <Divider />

      {/* ── Chart body ─────────────────────────────────────────────────────── */}
      <DialogContent
        ref={chartRef}
        sx={{
          p: 2,
          height: fullscreen ? 'calc(100vh - 57px)' : '60vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ChatChartPanel chartType={chartType} chartData={chartData} height="100%" yAxisLabel={yAxisLabel} />
      </DialogContent>
    </Dialog>
  );
}
